import { supabase } from '../utils/supabase';
import { createRateLimitedCall } from '../utils/rateLimiter';

const limitedMembershipCall = createRateLimitedCall('api', { maxRequests: 30, windowMs: 60000 });

const parseRpc = (data, error, fallback) => {
  if (error) throw error;
  if (data?.status === 'error') {
    throw new Error(data.error || fallback);
  }
  return data;
};

export async function setClubMemberInactive(clubId, playerId) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('set_club_member_inactive', {
      p_club_id: clubId,
      p_player_id: playerId,
    });
    return parseRpc(data, error, 'Failed to set member inactive');
  });
}

export async function setClubMemberActive(clubId, playerId) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('set_club_member_active', {
      p_club_id: clubId,
      p_player_id: playerId,
    });
    return parseRpc(data, error, 'Failed to restore member');
  });
}

export async function setClubMemberTrial(clubId, playerId, isTrial) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('set_club_member_trial', {
      p_club_id: clubId,
      p_player_id: playerId,
      p_is_trial: Boolean(isTrial),
    });
    return parseRpc(data, error, 'Failed to update trial label');
  });
}

export async function requestClubReactivation(clubId) {
  if (!clubId) throw new Error('Club is required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('request_club_reactivation', {
      p_club_id: clubId,
    });
    return parseRpc(data, error, 'Failed to request reactivation');
  });
}

export async function withdrawClubReactivation(clubId) {
  if (!clubId) throw new Error('Club is required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('withdraw_club_reactivation', {
      p_club_id: clubId,
    });
    return parseRpc(data, error, 'Failed to withdraw reactivation request');
  });
}

export async function approveClubReactivation(clubId, playerId) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('approve_club_reactivation', {
      p_club_id: clubId,
      p_player_id: playerId,
    });
    return parseRpc(data, error, 'Failed to approve reactivation');
  });
}

export async function declineClubReactivation(clubId, playerId) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('decline_club_reactivation', {
      p_club_id: clubId,
      p_player_id: playerId,
    });
    return parseRpc(data, error, 'Failed to decline reactivation');
  });
}

export async function getClubReactivationRequests(clubId) {
  if (!clubId) return [];
  const { data, error } = await supabase.rpc('get_club_reactivation_requests', {
    p_club_id: clubId,
  });
  if (error) throw error;
  return data || [];
}

export async function listClubMemberNotes(clubId, playerId) {
  if (!clubId || !playerId) return [];
  const { data, error } = await supabase.rpc('list_club_member_notes', {
    p_club_id: clubId,
    p_player_id: playerId,
  });
  if (error) throw error;
  return data || [];
}

export async function addClubMemberNote(clubId, playerId, body) {
  if (!clubId || !playerId) throw new Error('Club and player are required');
  return limitedMembershipCall(async () => {
    const { data, error } = await supabase.rpc('add_club_member_note', {
      p_club_id: clubId,
      p_player_id: playerId,
      p_body: body,
    });
    return parseRpc(data, error, 'Failed to add note');
  });
}

export const isInactiveMembership = (membership) =>
  membership?.membership_status === 'inactive';
